import React from 'react';
import cn from 'classnames';
import DefaultSelect, { components as selectComponents } from 'react-select';
import type { ControlProps } from 'react-select';
import AsyncSelect from 'react-select/async';

import { getTranslation } from 'helpers/getTranslation';

import { Typography } from 'components/shared/Typography';
import { Icon } from 'components/shared/Icon';

import styles from './Select.module.scss';

const MODES = {
  default: 'default',
  portal: 'portal',
};

const TYPES = {
  default: DefaultSelect,
  async: AsyncSelect,
} as const;

type UnknownOption = {
  label?: unknown;
  value?: unknown;
  [key: string]: unknown;
};

/** Merge target for react-select internals + wrapper props */

type AnySelectProps = Record<string, any>;

type RsControlProps = ControlProps<UnknownOption, boolean>;

const Control = ({
  isFocused,
  haveError,
  menuIsOpen,
  ...restProps
}: RsControlProps & { haveError?: boolean }) => (
  <selectComponents.Control
    {...restProps}
    isFocused={isFocused}
    menuIsOpen={menuIsOpen}
    className={cn(styles.control, {
      [styles.controlHaveError]: haveError,
      [styles.controlOpen]: menuIsOpen,
    })}
  />
);

const DropdownIndicator = React.memo(() => (
  <Icon name="chevronDown" className={styles.indicator} />
));

const ClearIndicator = React.memo(
  ({ innerProps }: { innerProps: React.HTMLAttributes<HTMLDivElement> }) => (
    <Typography
      {...innerProps}
      variant="body3"
      weight="medium"
      className={styles.clear}
    >
      Clear
    </Typography>
  ),
);

const Placeholder = React.memo(
  ({
    innerProps,
    children,
  }: {
    innerProps: React.HTMLAttributes<HTMLParagraphElement>;
    children?: React.ReactNode;
  }) => (
    <Typography
      {...innerProps}
      className={styles.placeholder}
      variant="body2"
      mode="compact"
    >
      {children}
    </Typography>
  ),
);

const ValueContainer = React.memo(
  ({
    innerProps,
    children,
  }: {
    innerProps?: React.HTMLAttributes<HTMLDivElement>;
    children?: React.ReactNode;
  }) => <>{children}</>,
);

const SingleValue = React.memo((props: AnySelectProps) => {
  const {
    innerProps = {} as React.HTMLAttributes<HTMLParagraphElement>,
    children,
    valueRenderer,
    data,
  } = props as Record<string, unknown> & {
    innerProps?: React.HTMLAttributes<HTMLParagraphElement>;
    children?: React.ReactNode;
    valueRenderer?: (args: {
      data: any;
      innerProps: React.HTMLAttributes<HTMLParagraphElement>;
    }) => React.ReactNode;
    data?: UnknownOption;
  };
  if (valueRenderer) {
    return valueRenderer({
      data: data?.label,
      innerProps,
    }) as React.ReactElement;
  }

  return (
    <Typography
      {...innerProps}
      className={styles.value}
      variant="body2"
      mode="compact"
    >
      {children}
    </Typography>
  );
});

const MenuList = React.memo(
  (props: React.ComponentProps<typeof selectComponents.MenuList>) => (
    <selectComponents.MenuList {...props} className={styles.menuList} />
  ),
);

const Option = React.memo((props: AnySelectProps) => {
  const {
    innerProps = {} as React.HTMLAttributes<HTMLDivElement>,
    label,
    children,
    optionRenderer,
  } = props as Record<string, unknown> & {
    innerProps?: React.HTMLAttributes<HTMLDivElement>;
    label?: unknown;
    children?: React.ReactNode;
    optionRenderer?: (args: {
      data: any;
      innerProps: React.HTMLAttributes<HTMLDivElement>;
    }) => React.ReactNode;
  };

  if (optionRenderer) {
    return optionRenderer({
      data: label,
      innerProps,
    }) as React.ReactElement;
  }

  return (
    <div {...innerProps} className={styles.option}>
      {children}
    </div>
  );
});

const NoOptionsMessage = React.memo(
  ({
    innerProps,
  }: {
    innerProps: React.HTMLAttributes<HTMLParagraphElement>;
  }) => (
    <Typography {...innerProps} className={styles.noOption}>
      No options
    </Typography>
  ),
);

const MultiValueContainer = React.memo(
  ({
    innerProps,
    children,
  }: {
    innerProps: React.HTMLAttributes<HTMLDivElement>;
    children?: React.ReactNode;
  }) => (
    <div {...innerProps} className={styles.tag}>
      {children}
    </div>
  ),
);

const MultiValueLabel = React.memo(
  ({
    innerProps,
    children,
  }: {
    innerProps: React.HTMLAttributes<HTMLParagraphElement>;
    children?: React.ReactNode;
  }) => (
    <Typography
      {...innerProps}
      className={styles.label}
      variant="body2"
      weight="medium"
      mode="compact"
    >
      {children}
    </Typography>
  ),
);

const MultiValueRemove = React.memo(
  ({ innerProps }: { innerProps: React.HTMLAttributes<HTMLDivElement> }) => (
    <div {...innerProps} className={styles.removeContainer}>
      <Icon name="cross" className={styles.removeIcon} />
    </div>
  ),
);

type SelectOwnProps = {
  className?: string;
  loading?: boolean;
  multi?: boolean;
  searchable?: boolean;
  mode?: keyof typeof MODES;
  type?: keyof typeof TYPES;
  placeholder?: string;
  options?: readonly UnknownOption[];
  variant?: string;
  haveError?: boolean;
  optionRenderer?: (args: {
    data: any;
    innerProps: React.HTMLAttributes<HTMLDivElement>;
  }) => React.ReactNode;
  valueRenderer?: (args: {
    data: any;
    innerProps: React.HTMLAttributes<HTMLParagraphElement>;
  }) => React.ReactNode;
};

type SelectMergedProps = SelectOwnProps & Record<string, unknown>;

const Select = ({
  className,
  loading,
  multi = false,
  searchable = false,
  mode = 'portal',
  type = 'default',
  placeholder = getTranslation('common.text.select'),
  options,
  variant = 'outlined',
  haveError,
  optionRenderer,
  valueRenderer,
  ...restProps
}: SelectMergedProps) => {
  const components: AnySelectProps = {
    Control: (props: RsControlProps) => (
      <Control {...props} haveError={haveError} />
    ),
    DropdownIndicator,
    ClearIndicator,
    Placeholder,
    SingleValue: (valueProps: AnySelectProps) => (
      <SingleValue {...valueProps} valueRenderer={valueRenderer} />
    ),
    MultiValueLabel,
    MultiValueRemove,
    MenuList,
    Option: (optionProps: AnySelectProps) => (
      <Option {...optionProps} optionRenderer={optionRenderer} />
    ),
    NoOptionsMessage,
    MultiValueContainer,
    ValueContainer,
    IndicatorSeparator: null,
  };

  const commonProps: AnySelectProps = {
    isLoading: loading,
    isMulti: multi,
    isSearchable: searchable,
    hideSelectedOptions: !multi,
    closeMenuOnSelect: !multi,
    unstyled: true,
    placeholder: loading ? getTranslation('common.text.loading') : placeholder,
    components,
  };

  if (type === 'default') {
    commonProps.options = loading ? Object.freeze([]) : options;
  }

  if (mode === MODES.default) {
    commonProps.styles = { control: (): null => null };
  }

  if (mode === MODES.portal) {
    commonProps.styles = {
      control: (): null => null,
      menuPortal: (base: { zIndex?: number }) => ({
        ...base,
        zIndex: 9999,
      }),
    };
  }

  const SelectComponent = TYPES[type];

  switch (MODES[mode]) {
    case MODES.default:
      return (
        <SelectComponent {...commonProps} {...(restProps as AnySelectProps)} />
      );

    case MODES.portal:
      return (
        <SelectComponent
          {...commonProps}
          {...(restProps as AnySelectProps)}
          className={cn(styles.container, className)}
          menuPortalTarget={document.body}
          menuPlacement="auto"
          menuPosition="fixed"
          menuShouldBlockScroll
        />
      );
    default:
      return null;
  }
};

export default React.memo(Select);
